import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KnowledgeBaseDocument } from '@/types/supabase';
import KnowledgeBaseCreateDialog from '@/components/knowledge/KnowledgeBaseCreateDialog';
import KnowledgeBaseEditDialog from '@/components/knowledge/KnowledgeBaseEditDialog';
import KnowledgeBaseDeleteDialog from '@/components/knowledge/KnowledgeBaseDeleteDialog';
import DocumentDialog from '@/components/knowledge/DocumentDialog';
import DocumentDeleteDialog from '@/components/knowledge/DocumentDeleteDialog';
import ShareDialog from '@/components/knowledge/ShareDialog';
import { useDialogHandlers } from '@/components/knowledge/dialogs/useDialogHandlers';
import type { DocumentBatchProgress, UploadedSourceDraft } from '@/components/knowledge/documentBatch';
import { formatDuration, getSpinnerText } from '@/components/knowledge/documentBatch';
import { findDuplicateDocument } from '@/components/knowledge/documentDuplicates';
import type { LibrarianIngestResult } from '@/components/knowledge/types';
import { toast } from '@/components/ui/use-toast';

interface DialogsProviderProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editOpen: boolean;
  setEditOpen: (open: boolean) => void;
  deleteOpen: boolean;
  setDeleteOpen: (open: boolean) => void;
  deleteCount?: number;
  deleteLabel?: string;
  onDeleteKnowledgeBases?: () => Promise<boolean>;
  documentOpen: boolean;
  setDocumentOpen: (open: boolean) => void;
  editDocumentOpen: boolean;
  setEditDocumentOpen: (open: boolean) => void;
  deleteDocumentOpen: boolean;
  setDeleteDocumentOpen: (open: boolean) => void;
  shareDialogOpen: boolean;
  setShareDialogOpen: (open: boolean) => void;
  activeKnowledgeBase: any;
  activeDocument: KnowledgeBaseDocument | null;
  shares: any[];
  sharesLoading: boolean;
  shareEmail: string;
  setShareEmail: (email: string) => void;
  shareAccessLevel: 'View' | 'Edit' | 'Manage';
  setShareAccessLevel: (level: 'View' | 'Edit' | 'Manage') => void;
  fetchKnowledgeBaseDetails: () => Promise<void>;
  fetchKnowledgeBases: () => Promise<void>;
  onCreateKnowledgeBase?: (values: any) => Promise<void>;
  createCategoryLabel?: string | null;
  uploadAfterCreate?: boolean;
  setUploadAfterCreate?: (uploadAfterCreate: boolean) => void;
  onProcessWithLibrarian?: (document: KnowledgeBaseDocument) => Promise<LibrarianIngestResult | null>;
}

const DialogsProvider = ({
  open,
  setOpen,
  editOpen,
  setEditOpen,
  deleteOpen,
  setDeleteOpen,
  deleteCount = 1,
  deleteLabel,
  onDeleteKnowledgeBases,
  documentOpen,
  setDocumentOpen,
  editDocumentOpen,
  setEditDocumentOpen,
  deleteDocumentOpen,
  setDeleteDocumentOpen,
  shareDialogOpen,
  setShareDialogOpen,
  activeKnowledgeBase,
  activeDocument,
  shares,
  sharesLoading,
  shareEmail,
  setShareEmail,
  shareAccessLevel,
  setShareAccessLevel,
  fetchKnowledgeBaseDetails,
  fetchKnowledgeBases,
  onCreateKnowledgeBase,
  createCategoryLabel,
  uploadAfterCreate,
  setUploadAfterCreate,
  onProcessWithLibrarian,
}: DialogsProviderProps) => {
  const {
    onCreateKnowledgeBase: onCreateKnowledgeBaseFallback,
    onUpdateKnowledgeBase,
    onDeleteKnowledgeBase,
    onCreateDocument,
    onUpdateDocument,
    onDeleteDocument,
    onShareKnowledgeBase,
    handleUpdateShare,
    handleRemoveShare,
  } = useDialogHandlers(
    activeKnowledgeBase,
    activeDocument,
    shareEmail,
    shareAccessLevel,
    setShareEmail,
    setOpen,
    setEditOpen,
    setDeleteOpen,
    fetchKnowledgeBaseDetails,
    fetchKnowledgeBases
  );

  const [batchProgress, setBatchProgress] = useState<DocumentBatchProgress | null>(null);
  const batchRunningRef = useRef(false);
  const existingSourceDocuments = useMemo(() => (
    (activeKnowledgeBase?.documents ?? []).map((document: KnowledgeBaseDocument) => ({
      name: document.name,
      content: document.content,
      size: document.size,
    }))
  ), [activeKnowledgeBase?.documents]);

  useEffect(() => {
    if (!batchProgress || batchProgress.status !== 'running') {
      return;
    }

    const intervalId = window.setInterval(() => {
      setBatchProgress((current) => (
        current && current.status === 'running'
          ? { ...current, spinnerText: getSpinnerText() }
          : current
      ));
    }, 2800);

    return () => window.clearInterval(intervalId);
  }, [batchProgress]);

  useEffect(() => {
    if (!documentOpen || batchProgress?.status !== 'completed') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setDocumentOpen(false);
      setBatchProgress(null);
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [batchProgress?.status, documentOpen, setDocumentOpen]);

  useEffect(() => {
    if (documentOpen || batchProgress?.status === 'running') {
      return;
    }

    setBatchProgress(null);
  }, [batchProgress?.status, documentOpen]);

  const notifyBatchComplete = useCallback((title: string, message: string, destructive = false) => {
    toast({
      title,
      description: message,
      variant: destructive ? 'destructive' : 'default',
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('operator:notify', { detail: { title, message } }));
    }
  }, []);

  const createDocumentWithDuplicateCheck = useCallback(async (
    values: { name: string; content: string },
    fileType: string,
    size: number,
    knownDocuments = existingSourceDocuments,
    showToast = true,
  ) => {
    const duplicate = findDuplicateDocument(knownDocuments, {
      name: values.name,
      content: values.content,
      size,
    });

    if (duplicate) {
      if (showToast) {
        toast({
          title: 'Duplicate paper skipped',
          description: `${values.name} is already in this knowledge base, so it was not uploaded again.`,
        });
      }
      return null;
    }

    return onCreateDocument(values, fileType, size);
  }, [existingSourceDocuments, onCreateDocument]);

  const startBatchProcessing = useCallback(async (sources: UploadedSourceDraft[], shouldProcessWithLibrarian: boolean) => {
    if (batchRunningRef.current || sources.length === 0) {
      return false;
    }

    const startedAt = Date.now();
    batchRunningRef.current = true;

    setBatchProgress({
      status: 'running',
      totalSources: sources.length,
      savedCount: 0,
      processedCount: 0,
      completedCount: 0,
      failedCount: 0,
      skippedCount: 0,
      currentIndex: 0,
      currentSourceName: sources[0]?.name ?? '',
      currentStep: 'saving',
      processingStatus: `Saving source 1 of ${sources.length}: ${sources[0]?.name ?? ''}`,
      spinnerText: getSpinnerText(),
      startedAt,
      averageItemMs: null,
      estimatedRemainingMs: null,
    });

    void (async () => {
      const knownDocuments = [...existingSourceDocuments];
      const batchSeenSources: Array<{ name: string; content: string; size: number }> = [];
      let savedCount = 0;
      let processedCount = 0;
      let failedCount = 0;
      let skippedCount = 0;
      let completedCount = 0;
      let elapsedTotalMs = 0;

      try {
        for (const [index, source] of sources.entries()) {
          const itemStartedAt = Date.now();
          const duplicate = findDuplicateDocument([...knownDocuments, ...batchSeenSources], {
            name: source.name,
            content: source.content,
            size: source.size,
          });

          if (duplicate) {
            skippedCount += 1;
            completedCount += 1;
            elapsedTotalMs += Date.now() - itemStartedAt;
            const averageItemMs = elapsedTotalMs / completedCount;
            const estimatedRemainingMs = averageItemMs * (sources.length - completedCount);

            setBatchProgress((current) => current ? {
              ...current,
              skippedCount,
              completedCount,
              currentIndex: index,
              currentSourceName: source.name,
              currentStep: 'skipping',
              processingStatus: `Skipping duplicate ${index + 1} of ${sources.length}: ${source.name}`,
              averageItemMs,
              estimatedRemainingMs,
            } : current);
            continue;
          }

          batchSeenSources.push({
            name: source.name,
            content: source.content,
            size: source.size,
          });

          setBatchProgress((current) => current ? {
            ...current,
            currentIndex: index,
            currentSourceName: source.name,
            currentStep: 'saving',
            processingStatus: `Saving source ${index + 1} of ${sources.length}: ${source.name}`,
          } : current);

          const savedDocument = await createDocumentWithDuplicateCheck(
            { name: source.name, content: source.content },
            source.fileType,
            source.size,
            knownDocuments,
            false,
          );

          if (savedDocument) {
            savedCount += 1;
            knownDocuments.push({
              name: source.name,
              content: source.content,
              size: source.size,
            });

            if (shouldProcessWithLibrarian && onProcessWithLibrarian) {
              setBatchProgress((current) => current ? {
                ...current,
                savedCount,
                skippedCount,
                currentIndex: index,
                currentSourceName: source.name,
                currentStep: 'processing',
                processingStatus: `Processing with Librarian ${index + 1} of ${sources.length}: ${source.name}`,
              } : current);

              const result = await onProcessWithLibrarian(savedDocument);
              if (result) {
                processedCount += 1;
              }
            }
          } else {
            failedCount += 1;
          }

          completedCount += 1;
          elapsedTotalMs += Date.now() - itemStartedAt;
          const averageItemMs = elapsedTotalMs / completedCount;
          const estimatedRemainingMs = averageItemMs * (sources.length - completedCount);

          setBatchProgress((current) => current ? {
            ...current,
            savedCount,
            processedCount,
            failedCount,
            skippedCount,
            completedCount,
            averageItemMs,
            estimatedRemainingMs,
          } : current);
        }

        const completedAt = Date.now();
        const summaryParts = [
          `${savedCount} of ${sources.length} sources were saved`,
          skippedCount > 0 ? `${skippedCount} duplicates were skipped` : null,
          shouldProcessWithLibrarian && onProcessWithLibrarian ? `Librarian processed ${processedCount}` : null,
          `Total time ${formatDuration(completedAt - startedAt)}.`,
        ].filter(Boolean);
        const summary = summaryParts.join('. ').replace(/\. Total time/, ' Total time');

        setBatchProgress({
          status: failedCount > 0 ? 'failed' : 'completed',
          totalSources: sources.length,
          savedCount,
          processedCount,
          completedCount,
          failedCount,
          skippedCount,
          currentIndex: sources.length - 1,
          currentSourceName: '',
          currentStep: 'finishing',
          processingStatus: failedCount > 0 ? 'Librarian batch finished with some failures.' : 'Librarian batch complete.',
          spinnerText: getSpinnerText(),
          startedAt,
          averageItemMs: completedCount > 0 ? elapsedTotalMs / completedCount : null,
          estimatedRemainingMs: 0,
        });

        notifyBatchComplete(
          failedCount > 0 ? 'Librarian processing finished with issues' : 'Librarian processing complete',
          summary,
          failedCount > 0
        );
      } catch (error) {
        console.error('Librarian batch processing failed:', error);

        setBatchProgress((current) => current ? {
          ...current,
          status: 'failed',
          currentStep: 'finishing',
          processingStatus: 'Librarian batch failed before completion.',
        } : current);

        notifyBatchComplete(
          'Librarian processing failed',
          error instanceof Error ? error.message : 'The Librarian batch stopped unexpectedly.',
          true
        );
      } finally {
        batchRunningRef.current = false;
      }
    })();

    return true;
  }, [createDocumentWithDuplicateCheck, existingSourceDocuments, notifyBatchComplete, onProcessWithLibrarian]);

  return (
    <>
      <KnowledgeBaseCreateDialog
        open={open}
        onOpenChange={setOpen}
        onSubmit={onCreateKnowledgeBase ?? onCreateKnowledgeBaseFallback}
        categoryLabel={createCategoryLabel}
        uploadAfterCreate={uploadAfterCreate}
        setUploadAfterCreate={setUploadAfterCreate}
      />

      <KnowledgeBaseEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        onSubmit={onUpdateKnowledgeBase}
        knowledgeBase={activeKnowledgeBase}
      />

      <KnowledgeBaseDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        knowledgeBaseName={deleteLabel}
        count={deleteCount}
        onConfirm={async () => {
          if (onDeleteKnowledgeBases) {
            return onDeleteKnowledgeBases();
          }

          await onDeleteKnowledgeBase();
          return true;
        }}
      />

      <DocumentDialog
        mode="create"
        open={documentOpen}
        onOpenChange={setDocumentOpen}
        onSubmit={createDocumentWithDuplicateCheck}
        onProcessWithLibrarian={onProcessWithLibrarian}
        onStartBatchProcessing={startBatchProcessing}
        batchProgress={batchProgress}
      />

      <DocumentDialog
        mode="edit"
        open={editDocumentOpen}
        onOpenChange={(nextOpen) => {
          setEditDocumentOpen(nextOpen);
        }}
        onSubmit={async (values, fileType, size) => {
          const updated = await onUpdateDocument(values, fileType, size);
          if (updated) {
            setEditDocumentOpen(false);
          }
          return updated;
        }}
        document={activeDocument}
      />

      <DocumentDeleteDialog
        open={deleteDocumentOpen}
        onOpenChange={setDeleteDocumentOpen}
        onConfirm={async () => {
          const deleted = await onDeleteDocument();
          if (deleted) {
            setDeleteDocumentOpen(false);
          }
        }}
      />

      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        shares={shares}
        sharesLoading={sharesLoading}
        shareEmail={shareEmail}
        setShareEmail={setShareEmail}
        shareAccessLevel={shareAccessLevel}
        setShareAccessLevel={setShareAccessLevel}
        onShare={onShareKnowledgeBase}
        onUpdateShare={handleUpdateShare}
        onRemoveShare={handleRemoveShare}
      />
    </>
  );
};

export default DialogsProvider;